import React, { useEffect, useState, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Mail, ArrowRight, ArrowLeft, Check, Globe, Gift, Sparkles, Users, MessageCircle, Copy, Crown, Shield, Lock, ChevronLeft, ChevronRight, UserPlus, Calendar, Repeat, Heart } from 'lucide-react';
type OnboardingStep = 'landing' | 'auth' | 'identity' | 'referral' | 'language' | 'reward' | 'packet' | 'explain' | 'misl' | 'share' | 'complete';
interface TreasuryOnboardingProps {
  isOpen: boolean;
  onClose: () => void;
  onComplete: () => void;
  /** When the modal is opened from an invite link, the inviter's name. */
  invitedBy?: string;
  /** When opened from an invite link, the misl the inviter belongs to. */
  invitedMisl?: string;
  /** When opened from an invite link, the raw referral code from the URL. */
  inviteCode?: string;
}
export function TreasuryOnboarding({
  isOpen,
  onClose,
  onComplete,
  invitedBy,
  invitedMisl,
  inviteCode
}: TreasuryOnboardingProps) {
  const [step, setStep] = useState<OnboardingStep>('landing');
  const [email, setEmail] = useState('');
  const [language, setLanguage] = useState<'en' | 'pa' | 'both'>('en');
  const [referralCode, setReferralCode] = useState('');
  const [packetState, setPacketState] = useState<'sealed' | 'opening' | 'revealed'>('sealed');
  const [mislView, setMislView] = useState<'choose' | 'browse' | 'confirm-invite' | 'confirm-later'>('choose');
  const [showSkipShareConfirm, setShowSkipShareConfirm] = useState(false);
  const [selectedMisl, setSelectedMisl] = useState<string | null>(null);
  const misls = [
  // Homeland / Region
  {
    name: 'Lahore',
    leader: '@lahore_s',
    members: '2,100',
    xp: '720k',
    tag: 'Homeland · Founding city',
    region: 'Punjab · Pakistan'
  }, {
    name: 'Amritsar',
    leader: '@golden_s',
    members: '1,360',
    xp: '510k',
    tag: 'Spiritual heart',
    region: 'Punjab · India'
  }, {
    name: 'Majha',
    leader: '@majha_s',
    members: '960',
    xp: '370k',
    tag: 'Heartland',
    region: 'Central Punjab'
  }, {
    name: 'Doaba',
    leader: '@doaba_k',
    members: '780',
    xp: '300k',
    tag: 'Rivers between',
    region: 'Beas–Sutlej'
  }, {
    name: 'Malwa',
    leader: '@malwa_s',
    members: '820',
    xp: '320k',
    tag: 'Southern strength',
    region: 'South Punjab'
  },
  // Diaspora / City
  {
    name: 'Brampton',
    leader: '@brampton_s',
    members: '1,420',
    xp: '460k',
    tag: 'Diaspora · Canada',
    region: 'Ontario'
  }, {
    name: 'Surrey',
    leader: '@surrey_s',
    members: '1,180',
    xp: '410k',
    tag: 'Diaspora · Canada',
    region: 'British Columbia'
  }, {
    name: 'Southall',
    leader: '@southall_s',
    members: '980',
    xp: '360k',
    tag: 'Diaspora · UK',
    region: 'London'
  }, {
    name: 'Toronto',
    leader: '@toronto_s',
    members: '1,260',
    xp: '440k',
    tag: 'Diaspora · Canada',
    region: 'Ontario'
  }, {
    name: 'Vancouver',
    leader: '@van_s',
    members: '1,040',
    xp: '390k',
    tag: 'Diaspora · Canada',
    region: 'British Columbia'
  },
  // Family / Clan
  {
    name: 'Sandhu',
    leader: '@sandhu_s',
    members: '1,180',
    xp: '420k',
    tag: 'Jat clan',
    region: 'Family · Lineage'
  }, {
    name: 'Gill',
    leader: '@gill_s',
    members: '1,340',
    xp: '470k',
    tag: 'Jat clan',
    region: 'Family · Lineage'
  }, {
    name: 'Sidhu',
    leader: '@sidhu_s',
    members: '1,220',
    xp: '440k',
    tag: 'Jat clan',
    region: 'Family · Lineage'
  }, {
    name: 'Dhillon',
    leader: '@dhillon_s',
    members: '960',
    xp: '350k',
    tag: 'Jat clan',
    region: 'Family · Lineage'
  }, {
    name: 'Brar',
    leader: '@brar_s',
    members: '880',
    xp: '320k',
    tag: 'Jat clan',
    region: 'Family · Lineage'
  },
  // Historic Prestige
  {
    name: 'Sukerchakia',
    leader: '@ranjit_s',
    members: '1,520',
    xp: '480k',
    tag: 'Founder favorite',
    region: 'Gujranwala'
  }, {
    name: 'Ahluwalia',
    leader: '@jassa_singh',
    members: '1,240',
    xp: '450k',
    tag: 'Most active',
    region: 'Kapurthala'
  }, {
    name: 'Ramgarhia',
    leader: '@jassa_r',
    members: '1,180',
    xp: '430k',
    tag: 'Builders',
    region: 'Sri Hargobindpur'
  }, {
    name: 'Kanheya',
    leader: '@jai_singh',
    members: '850',
    xp: '310k',
    tag: 'New & growing',
    region: 'Sohian'
  }, {
    name: 'Nakai',
    leader: '@hira_s',
    members: '620',
    xp: '240k',
    tag: 'Rising',
    region: 'Chunian'
  }];
  // Reset step + transient state when opened.
  // When opened from an invite link (invitedBy present), skip the landing step
  // and pre-fill the referral code + selected misl so the flow feels personal.
  useEffect(() => {
    if (isOpen) {
      if (invitedBy) {
        setStep('auth');
        if (inviteCode) setReferralCode(inviteCode);
        if (invitedMisl) setSelectedMisl(invitedMisl);
      } else {
        setStep('landing');
        setSelectedMisl(null);
      }
      setPacketState('sealed');
      setMislView('choose');
    }
  }, [isOpen, invitedBy, invitedMisl, inviteCode]);
  // Always reset the packet animation whenever the user lands on the packet step,
  // so the sealed → opening → revealed sequence plays every time (forward or back).
  useEffect(() => {
    if (step === 'packet') setPacketState('sealed');
  }, [step]);
  const stepOrder: OnboardingStep[] = ['landing', 'auth', 'identity', 'referral', 'language', 'reward', 'packet', 'explain', 'misl', 'share', 'complete'];
  const currentIndex = stepOrder.indexOf(step);
  const nextStep = () => {
    if (currentIndex < stepOrder.length - 1) {
      setStep(stepOrder[currentIndex + 1]);
    } else {
      onComplete();
    }
  };
  const prevStep = () => {
    if (currentIndex > 0) {
      // Reset packet state if going back to packet step
      if (stepOrder[currentIndex - 1] === 'packet') setPacketState('sealed');
      // Reset misl sub-view if going back to or away from misl step
      if (stepOrder[currentIndex - 1] === 'misl' || step === 'misl') {
        setMislView('choose');
        setSelectedMisl(null);
      }
      setStep(stepOrder[currentIndex - 1]);
    }
  };
  if (!isOpen) return null;
  return <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6" data-id="element-2899">
      <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} className="absolute inset-0 bg-[#0B0F19]/95 backdrop-blur-md" onClick={onClose} data-id="element-2900" />

      <motion.div initial={{
      opacity: 0,
      scale: 0.95,
      y: 20
    }} animate={{
      opacity: 1,
      scale: 1,
      y: 0
    }} exit={{
      opacity: 0,
      scale: 0.95,
      y: 20
    }} className="relative w-full max-w-xl bg-[#131B2B] border border-[#D4AF37]/30 shadow-[0_0_50px_rgba(0,0,0,0.5)] overflow-hidden" data-id="element-2901">
        {/* Progress Bar */}
        <div className="absolute top-0 left-0 right-0 h-1 bg-[#0B0F19]" data-id="element-2902">
          <motion.div className="h-full bg-[#D4AF37]" initial={{
          width: '0%'
        }} animate={{
          width: `${['landing', 'auth', 'identity', 'referral', 'language', 'reward', 'packet', 'explain', 'misl', 'share', 'complete'].indexOf(step) / 10 * 100}%`
        }} data-id="element-2903" />
        </div>

        {/* Back button — hidden on first step */}
        {step !== 'landing' && step !== 'complete' && <button type="button" onClick={prevStep} className="absolute top-4 left-4 flex items-center gap-1.5 text-[#FDFBF7]/40 hover:text-[#D4AF37] transition-colors z-10 text-[10px] uppercase tracking-[0.25em] font-medium" aria-label="Go back" data-id="element-2904">
            <ChevronLeft className="w-4 h-4" data-id="element-2905" />
            Back
          </button>}

        <button type="button" onClick={onClose} className="absolute top-4 right-4 text-[#FDFBF7]/40 hover:text-[#FDFBF7] transition-colors z-10" aria-label="Close" data-id="element-2906">
          <X className="w-5 h-5" data-id="element-2907" />
        </button>

        {/* Invite banner — shown when modal is opened from an invite link */}
        {invitedBy && step !== 'complete' && <div className="bg-[#D4AF37]/10 border-b border-[#D4AF37]/30 px-4 sm:px-6 pt-9 sm:pt-10 pb-3 flex items-center gap-3" data-id="element-2908">
            <div className="w-8 h-8 rounded-full bg-[#D4AF37]/20 border border-[#D4AF37]/40 flex items-center justify-center flex-shrink-0" data-id="element-2909">
              <UserPlus className="w-4 h-4 text-[#D4AF37]" data-id="element-2910" />
            </div>
            <div className="text-left" data-id="element-2911">
              <p className="text-[#D4AF37] text-[9px] uppercase tracking-[0.3em] font-bold" data-id="element-2912">
                Invited by {invitedBy}
              </p>
              {invitedMisl && <p className="text-[#FDFBF7]/70 text-[11px] leading-tight" data-id="element-2913">
                  You'll join their misl —{' '}
                  <span className="text-[#D4AF37] font-medium" data-id="element-2914">
                    {invitedMisl}
                  </span>
                </p>}
            </div>
          </div>}

        <div className="p-8 sm:p-12" data-id="element-2915">
          <AnimatePresence mode="wait" data-id="element-2916">
            {/* Step 1: Landing Hook */}
            {step === 'landing' && <motion.div key="landing" initial={{
            opacity: 0,
            x: 20
          }} animate={{
            opacity: 1,
            x: 0
          }} exit={{
            opacity: 0,
            x: -20
          }} className="space-y-8" data-id="element-2917">
                <div className="space-y-4 text-center" data-id="element-2918">
                  <div className="inline-flex items-center justify-center w-16 h-16 border border-[#D4AF37] bg-[#0B0F19] mb-4" data-id="element-2919">
                    <Crown className="w-8 h-8 text-[#D4AF37]" data-id="element-2920" />
                  </div>
                  <h2 className="font-serif text-3xl sm:text-4xl text-[#FDFBF7]" data-id="element-2921">
                    Join Treasures of the Sikh Empire
                  </h2>
                  <p className="text-[#FDFBF7]/60 text-sm leading-relaxed max-w-md mx-auto" data-id="element-2922">
                    Open your daily Court Packet. Build your treasury. Rise in
                    the Lahore Court. Unlock real privileges at the live
                    experience.
                  </p>
                </div>

                <div className="flex flex-col gap-3" data-id="element-2923">
                  <button onClick={nextStep} className="w-full py-4 bg-[#D4AF37] text-[#0B0F19] text-xs uppercase tracking-[0.2em] font-bold hover:bg-[#E5C158] transition-colors flex items-center justify-center gap-2" data-id="element-2924">
                    Join the Treasury <ArrowRight className="w-4 h-4" data-id="element-2925" />
                  </button>
                  <button onClick={nextStep} className="w-full py-4 border border-[#D4AF37]/30 text-[#FDFBF7] text-xs uppercase tracking-[0.2em] font-medium hover:bg-[#D4AF37]/10 transition-colors" data-id="element-2926">
                    I Have an Invite
                  </button>
                </div>
              </motion.div>}

            {/* Step 2: Fast Account Creation */}
            {step === 'auth' && <motion.div key="auth" initial={{
            opacity: 0,
            x: 20
          }} animate={{
            opacity: 1,
            x: 0
          }} exit={{
            opacity: 0,
            x: -20
          }} className="space-y-8" data-id="element-2927">
                <div className="space-y-2" data-id="element-2928">
                  <h2 className="font-serif text-3xl text-[#FDFBF7]" data-id="element-2929">
                    Create Your Account
                  </h2>
                  <p className="text-[#FDFBF7]/60 text-sm" data-id="element-2930">
                    One tap to get in. Keep it light.
                  </p>
                </div>

                <div className="space-y-3" data-id="element-2931">
                  {/* Google — primary */}
                  <button type="button" onClick={nextStep} className="w-full py-4 bg-[#FDFBF7] text-[#0B0F19] text-sm font-medium hover:bg-white transition-colors flex items-center justify-center gap-3 shadow-lg" data-id="element-2932">
                    <svg className="w-5 h-5" viewBox="0 0 24 24" aria-hidden="true" data-id="element-2933">
                      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" data-id="element-2934" />
                      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" data-id="element-2935" />
                      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" data-id="element-2936" />
                      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" data-id="element-2937" />
                    </svg>
                    Continue with Google
                  </button>

                  {/* Apple — primary */}
                  <button type="button" onClick={nextStep} className="w-full py-4 bg-[#0B0F19] text-[#FDFBF7] text-sm font-medium hover:bg-[#1a2238] transition-colors flex items-center justify-center gap-3 border border-[#FDFBF7]/20" data-id="element-2938">
                    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" data-id="element-2939">
                      <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" data-id="element-2940" />
                    </svg>
                    Continue with Apple
                  </button>
                </div>

                {/* Divider */}
                <div className="relative py-2" data-id="element-2941">
                  <div className="absolute inset-0 flex items-center" data-id="element-2942">
                    <div className="w-full border-t border-[#D4AF37]/10" data-id="element-2943"></div>
                  </div>
                  <div className="relative flex justify-center text-[10px] uppercase tracking-widest text-[#FDFBF7]/30 bg-[#131B2B] px-3" data-id="element-2944">
                    Or use email
                  </div>
                </div>

                {/* Email fallback */}
                <div className="space-y-4" data-id="element-2945">
                  <div className="space-y-2" data-id="element-2946">
                    <label className="text-[10px] uppercase tracking-widest text-[#D4AF37]/60 font-bold" data-id="element-2947">
                      First Name
                    </label>
                    <input type="text" placeholder="Harnoor" className="w-full bg-[#0B0F19] border border-[#D4AF37]/20 px-4 py-3 text-[#FDFBF7] focus:outline-none focus:border-[#D4AF37] transition-colors" data-id="element-2948" />
                  </div>
                  <div className="space-y-2" data-id="element-2949">
                    <label className="text-[10px] uppercase tracking-widest text-[#D4AF37]/60 font-bold" data-id="element-2950">
                      Email or Mobile
                    </label>
                    <div className="relative" data-id="element-2951">
                      <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#D4AF37]/40" data-id="element-2952" />
                      <input type="text" value={email} onChange={e => setEmail(e.target.value)} placeholder="harnoor@example.com" className="w-full bg-[#0B0F19] border border-[#D4AF37]/20 pl-12 pr-4 py-3 text-[#FDFBF7] focus:outline-none focus:border-[#D4AF37] transition-colors" data-id="element-2953" />
                    </div>
                  </div>

                  <button type="button" onClick={nextStep} className="w-full py-3 border border-[#D4AF37]/40 text-[#D4AF37] text-xs uppercase tracking-[0.2em] font-medium hover:bg-[#D4AF37]/5 transition-colors flex items-center justify-center gap-2" data-id="element-2954">
                    Send Magic Link <ArrowRight className="w-4 h-4" data-id="element-2955" />
                  </button>
                </div>
              </motion.div>}

            {/* Step 3: Identity Moment */}
            {step === 'identity' && <motion.div key="identity" initial={{
            opacity: 0,
            x: 20
          }} animate={{
            opacity: 1,
            x: 0
          }} exit={{
            opacity: 0,
            x: -20
          }} className="space-y-8" data-id="element-2956">
                <div className="space-y-2" data-id="element-2957">
                  <h2 className="font-serif text-3xl text-[#FDFBF7]" data-id="element-2958">
                    Your Path to the Court
                  </h2>
                  <p className="text-[#FDFBF7]/60 text-sm" data-id="element-2959">
                    How are you entering the Treasury?
                  </p>
                </div>

                <div className="grid gap-3" data-id="element-2960">
                  {[{
                id: 'new',
                label: "I'm new to the treasury",
                icon: Sparkles
              }, {
                id: 'invited',
                label: 'I was invited by a friend',
                icon: UserPlus
              }].map(option => <button key={option.id} onClick={nextStep} className="group flex items-center gap-4 p-4 border border-[#D4AF37]/20 bg-[#0B0F19]/40 hover:border-[#D4AF37] hover:bg-[#D4AF37]/5 transition-all text-left" data-id="element-2961">
                      <div className="w-10 h-10 border border-[#D4AF37]/30 flex items-center justify-center group-hover:border-[#D4AF37] transition-colors" data-id="element-2962">
                        <option.icon className="w-5 h-5 text-[#D4AF37]/60 group-hover:text-[#D4AF37]" data-id="element-2963" />
                      </div>
                      <span className="text-[#FDFBF7] text-sm font-medium" data-id="element-2964">
                        {option.label}
                      </span>
                      <ChevronRight className="ml-auto w-4 h-4 text-[#D4AF37]/40 group-hover:text-[#D4AF37]" data-id="element-2965" />
                    </button>)}
                </div>
              </motion.div>}

            {/* Step 4: Invite Capture */}
            {step === 'referral' && <motion.div key="referral" initial={{
            opacity: 0,
            x: 20
          }} animate={{
            opacity: 1,
            x: 0
          }} exit={{
            opacity: 0,
            x: -20
          }} className="space-y-8" data-id="element-2966">
                <div className="space-y-2" data-id="element-2967">
                  <h2 className="font-serif text-3xl text-[#FDFBF7]" data-id="element-2968">
                    Enter Referral Code
                  </h2>
                  <p className="text-[#FDFBF7]/60 text-sm" data-id="element-2969">
                    If you were invited, enter your code to unlock a bonus
                    packet.
                  </p>
                </div>

                <div className="space-y-6" data-id="element-2970">
                  <input type="text" value={referralCode} onChange={e => setReferralCode(e.target.value.toUpperCase())} placeholder="LAHORE-2024" className="w-full bg-[#0B0F19] border border-[#D4AF37]/20 px-6 py-4 text-[#FDFBF7] text-center text-xl tracking-[0.2em] font-mono focus:outline-none focus:border-[#D4AF37] transition-colors" data-id="element-2971" />

                  <div className="flex gap-3" data-id="element-2972">
                    <button onClick={nextStep} className="flex-1 py-4 border border-[#D4AF37]/30 text-[#FDFBF7]/60 text-xs uppercase tracking-[0.2em] font-medium hover:bg-[#D4AF37]/5 transition-colors" data-id="element-2973">
                      Skip
                    </button>
                    <button onClick={nextStep} className="flex-[2] py-4 bg-[#D4AF37] text-[#0B0F19] text-xs uppercase tracking-[0.2em] font-bold hover:bg-[#E5C158] transition-colors flex items-center justify-center gap-2" data-id="element-2974">
                      Apply Code <Check className="w-4 h-4" data-id="element-2975" />
                    </button>
                  </div>
                </div>
              </motion.div>}

            {/* Step 5: Language Selection */}
            {step === 'language' && <motion.div key="language" initial={{
            opacity: 0,
            x: 20
          }} animate={{
            opacity: 1,
            x: 0
          }} exit={{
            opacity: 0,
            x: -20
          }} className="space-y-8" data-id="element-2976">
                <div className="space-y-2" data-id="element-2977">
                  <h2 className="font-serif text-3xl text-[#FDFBF7]" data-id="element-2978">
                    Choose Your Language
                  </h2>
                  <p className="text-[#FDFBF7]/60 text-sm" data-id="element-2979">
                    Select how you'd like to experience the Treasury.
                  </p>
                </div>

                <div className="grid gap-3" data-id="element-2980">
                  {[{
                id: 'en',
                label: 'English',
                sub: 'Standard experience'
              }, {
                id: 'pa',
                label: 'ਪੰਜਾਬੀ (Punjabi)',
                sub: 'Native experience'
              }, {
                id: 'both',
                label: 'Both / ਦੋਵੇਂ',
                sub: 'Bilingual experience'
              }].map(opt => <button key={opt.id} onClick={() => {
                setLanguage(opt.id as any);
                nextStep();
              }} className={`flex items-center gap-4 p-5 border transition-all text-left ${language === opt.id ? 'border-[#D4AF37] bg-[#D4AF37]/10' : 'border-[#D4AF37]/20 bg-[#0B0F19]/40 hover:border-[#D4AF37]/50'}`} data-id="element-2981">
                      <Globe className={`w-5 h-5 ${language === opt.id ? 'text-[#D4AF37]' : 'text-[#D4AF37]/40'}`} data-id="element-2982" />
                      <div data-id="element-2983">
                        <p className="text-[#FDFBF7] text-sm font-medium" data-id="element-2984">
                          {opt.label}
                        </p>
                        <p className="text-[#FDFBF7]/40 text-[10px] uppercase tracking-widest mt-0.5" data-id="element-2985">
                          {opt.sub}
                        </p>
                      </div>
                      {language === opt.id && <Check className="ml-auto w-4 h-4 text-[#D4AF37]" data-id="element-2986" />}
                    </button>)}
                </div>
              </motion.div>}

            {/* Step 6: Welcome Reward */}
            {step === 'reward' && <motion.div key="reward" initial={{
            opacity: 0,
            scale: 0.9
          }} animate={{
            opacity: 1,
            scale: 1
          }} exit={{
            opacity: 0,
            scale: 1.1
          }} className="space-y-8 text-center" data-id="element-2987">
                <div className="space-y-2" data-id="element-2988">
                  <p className="text-[#D4AF37] text-[10px] uppercase tracking-[0.3em] font-bold" data-id="element-2989">
                    Welcome to the Treasures of the Sikh Empire
                  </p>
                  <h2 className="font-serif text-3xl text-[#FDFBF7]" data-id="element-2990">
                    Your Collection Awaits
                  </h2>
                </div>

                <div className="relative py-2" data-id="element-2991">
                  <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_rgba(212,175,55,0.25),_transparent_65%)] blur-3xl" data-id="element-2992" />
                  <motion.div animate={{
                y: [0, -6, 0]
              }} transition={{
                duration: 4,
                repeat: Infinity,
                ease: 'easeInOut'
              }} className="relative w-52 sm:w-60 mx-auto" data-id="element-2993">
                    <div className="absolute -inset-2 bg-gradient-to-b from-[#D4AF37]/40 via-transparent to-[#D4AF37]/40 blur-xl" data-id="element-2994" />
                    <img src="https://cdn.magicpatterns.com/uploads/3hBK2oMvmeZZy5P54xmeKQ/Lahore_Court_Collection.png" alt="Lahore Court Collection — Founders' Edition" className="relative w-full drop-shadow-[0_20px_50px_rgba(0,0,0,0.8)]" data-id="element-2995" />
                    <motion.div initial={{
                  opacity: 0,
                  y: 10
                }} animate={{
                  opacity: 1,
                  y: 0
                }} transition={{
                  delay: 0.4
                }} className="absolute -bottom-2 left-1/2 -translate-x-1/2 bg-[#D4AF37] text-[#0B0F19] px-4 py-1.5 text-[9px] uppercase tracking-[0.25em] font-bold whitespace-nowrap shadow-lg" data-id="element-2996">
                      Founders' Edition · Unlocked
                    </motion.div>
                  </motion.div>
                </div>

                <p className="text-[#FDFBF7]/70 text-sm max-w-sm mx-auto leading-relaxed pt-6" data-id="element-2997">
                  You have unlocked the{' '}
                  <span className="text-[#D4AF37] font-bold" data-id="element-2998">
                    Lahore Court Collection
                  </span>
                  . Eight founding faces of sovereignty are waiting to be
                  claimed — starting with your first packet.
                </p>

                <button type="button" onClick={nextStep} className="w-full py-4 bg-[#D4AF37] text-[#0B0F19] text-xs uppercase tracking-[0.2em] font-bold hover:bg-[#E5C158] transition-colors flex items-center justify-center gap-2 shadow-[0_0_30px_rgba(212,175,55,0.3)] group" data-id="element-2999">
                  Claim Your First Packet
                  <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" data-id="element-3000" />
                </button>
              </motion.div>}

            {/* Step 7: First Packet Opening */}
            {step === 'packet' && <motion.div key="packet" initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} exit={{
            opacity: 0
          }} className="text-center min-h-[500px] flex flex-col items-center justify-center relative" data-id="element-3001">
                <AnimatePresence mode="wait" data-id="element-3002">
                  {/* ============ SEALED ============ */}
                  {packetState === 'sealed' && <motion.div key="sealed" initial={{
                opacity: 0,
                y: 20
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                scale: 0.95
              }} transition={{
                duration: 0.5
              }} className="flex flex-col items-center text-center space-y-7 w-full" data-id="element-3003">
                      <div className="space-y-2" data-id="element-3004">
                        <p className="text-[#8B0000] text-[10px] uppercase tracking-[0.3em] font-bold" data-id="element-3005">
                          First Reward
                        </p>
                        <h2 className="font-serif text-3xl text-[#FDFBF7]" data-id="element-3006">
                          The Darbar Awaits
                        </h2>
                      </div>

                      <motion.div animate={{
                  y: [0, -8, 0]
                }} transition={{
                  duration: 3.5,
                  repeat: Infinity,
                  ease: 'easeInOut'
                }} className="relative w-56 sm:w-64" data-id="element-3007">
                        <motion.div animate={{
                    opacity: [0.4, 0.7, 0.4]
                  }} transition={{
                    duration: 2.8,
                    repeat: Infinity
                  }} className="absolute inset-0 bg-[radial-gradient(circle_at_center,_rgba(212,175,55,0.4),_transparent_65%)] blur-2xl pointer-events-none" data-id="element-3008" />
                        <motion.button type="button" onClick={() => {
                    setPacketState('opening');
                    setTimeout(() => setPacketState('revealed'), 1900);
                  }} whileHover={{
                    scale: 1.04
                  }} whileTap={{
                    scale: 0.96
                  }} aria-label="Break the seal and open your first packet" className="relative block w-full cursor-pointer" data-id="element-3009">
                          <img src="https://cdn.magicpatterns.com/uploads/9o4p7UEYp4VE7S65Drh7mS/envelope.png" alt="Sealed envelope" className="w-full drop-shadow-[0_25px_50px_rgba(0,0,0,0.7)]" data-id="element-3010" />
                          <span className="absolute -bottom-3 left-1/2 -translate-x-1/2 text-[#D4AF37]/60 text-[9px] tracking-[0.3em] uppercase whitespace-nowrap" data-id="element-3011">
                            Click to open
                          </span>
                        </motion.button>
                      </motion.div>

                      <p className="text-[#FDFBF7]/60 text-sm font-light max-w-sm mx-auto leading-relaxed" data-id="element-3012">
                        A sealed envelope from the Maharaja's court awaits.
                        Break the wax — discover the artifact within.
                      </p>

                      <motion.button type="button" whileHover={{
                  scale: 1.02
                }} whileTap={{
                  scale: 0.98
                }} onClick={() => {
                  setPacketState('opening');
                  setTimeout(() => setPacketState('revealed'), 1900);
                }} className="px-10 py-4 bg-[#8B0000] hover:bg-[#A00000] text-[#FDFBF7] text-xs tracking-[0.3em] uppercase font-medium shadow-2xl transition-colors flex items-center gap-3" data-id="element-3013">
                        <Sparkles className="w-4 h-4" data-id="element-3014" /> Break the Seal
                      </motion.button>
                    </motion.div>}

                  {/* ============ OPENING ============ */}
                  {packetState === 'opening' && <motion.div key="opening" initial={{
                opacity: 0
              }} animate={{
                opacity: 1
              }} exit={{
                opacity: 0
              }} className="relative flex items-center justify-center w-full min-h-[440px]" data-id="element-3015">
                      {/* Expanding gold burst */}
                      <motion.div initial={{
                  scale: 0.4,
                  opacity: 0
                }} animate={{
                  scale: 4,
                  opacity: [0, 0.9, 0]
                }} transition={{
                  duration: 1.8,
                  ease: 'easeOut'
                }} className="absolute w-80 h-80 rounded-full bg-[radial-gradient(circle,_rgba(212,175,55,0.6),_transparent_60%)] blur-3xl" data-id="element-3016" />

                      {/* Shaking + shrinking envelope */}
                      <motion.div initial={{
                  scale: 1,
                  opacity: 1
                }} animate={{
                  scale: [1, 1.1, 0.6],
                  opacity: [1, 1, 0],
                  rotateZ: [0, -3, 4, -2, 0]
                }} transition={{
                  duration: 1.8,
                  times: [0, 0.5, 1],
                  ease: 'easeInOut'
                }} className="relative w-56 sm:w-64 z-10" data-id="element-3017">
                        <img src="https://cdn.magicpatterns.com/uploads/9o4p7UEYp4VE7S65Drh7mS/envelope.png" alt="Opening envelope" className="w-full drop-shadow-[0_0_80px_rgba(212,175,55,0.7)]" data-id="element-3018" />
                      </motion.div>

                      {/* Radial sparkle particles */}
                      {Array.from({
                  length: 20
                }).map((_, i) => {
                  const angle = i / 20 * Math.PI * 2;
                  const distance = 220;
                  return <motion.div key={i} initial={{
                    x: 0,
                    y: 0,
                    opacity: 0,
                    scale: 0
                  }} animate={{
                    x: Math.cos(angle) * distance,
                    y: Math.sin(angle) * distance,
                    opacity: [0, 1, 0],
                    scale: [0, 1.2, 0]
                  }} transition={{
                    duration: 1.6,
                    delay: 0.2,
                    ease: 'easeOut'
                  }} className="absolute w-1.5 h-1.5 bg-[#D4AF37] rounded-full shadow-[0_0_10px_rgba(212,175,55,1)]" data-id="element-3019" />;
                })}
                    </motion.div>}

                  {/* ============ REVEALED ============ */}
                  {packetState === 'revealed' && <motion.div key="revealed" initial={{
                opacity: 0,
                scale: 0.8
              }} animate={{
                opacity: 1,
                scale: 1
              }} transition={{
                duration: 0.6
              }} className="space-y-8 w-full" data-id="element-3020">
                      <div className="space-y-2" data-id="element-3021">
                        <p className="text-[#D4AF37] text-[10px] uppercase tracking-[0.3em] font-bold" data-id="element-3022">
                          Premium Reveal
                        </p>
                        <h2 className="font-serif text-3xl text-[#FDFBF7]" data-id="element-3023">
                          Ranjit Singh
                        </h2>
                      </div>

                      <motion.div initial={{
                  rotateY: 180,
                  opacity: 0
                }} animate={{
                  rotateY: 0,
                  opacity: 1
                }} transition={{
                  duration: 0.8,
                  delay: 0.2,
                  ease: 'easeOut'
                }} className="relative w-52 aspect-[3/4] mx-auto border-2 border-[#D4AF37] shadow-[0_0_50px_rgba(212,175,55,0.5)] overflow-hidden" data-id="element-3024">
                        <img src="https://cdn.magicpatterns.com/uploads/8Kp4p7xwsPGGj2bxMr4kRM/Ranjit_Singh.png" alt="Ranjit Singh" className="w-full h-full object-cover" data-id="element-3025" />
                        <div className="absolute top-2 left-2 px-2 py-1 bg-[#D4AF37] text-[#0B0F19] text-[8px] uppercase tracking-widest font-bold" data-id="element-3026">
                          Founders' Edition
                        </div>
                      </motion.div>

                      <div className="space-y-6" data-id="element-3027">
                        <p className="text-[#FDFBF7]/60 text-sm leading-relaxed max-w-xs mx-auto" data-id="element-3028">
                          You have received{' '}
                          <span className="text-[#D4AF37] font-bold" data-id="element-3029">
                            The Lion of Punjab
                          </span>
                          . This is a rare starter collectible to begin your
                          treasury.
                        </p>
                        <button type="button" onClick={nextStep} className="w-full py-4 bg-[#D4AF37] text-[#0B0F19] text-xs uppercase tracking-[0.2em] font-bold hover:bg-[#E5C158] transition-colors flex items-center justify-center gap-2 group" data-id="element-3030">
                          Continue
                          <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" data-id="element-3031" />
                        </button>
                      </div>
                    </motion.div>}
                </AnimatePresence>
              </motion.div>}

            {/* Step 8: Quick Explain */}
            {step === 'explain' && <motion.div key="explain" initial={{
            opacity: 0,
            x: 20
          }} animate={{
            opacity: 1,
            x: 0
          }} exit={{
            opacity: 0,
            x: -20
          }} className="space-y-10" data-id="element-3032">
                <div className="space-y-2 text-center" data-id="element-3033">
                  <h2 className="font-serif text-3xl text-[#FDFBF7]" data-id="element-3034">
                    How it Works
                  </h2>
                  <p className="text-[#FDFBF7]/60 text-sm" data-id="element-3035">
                    The path to sovereignty is simple.
                  </p>
                </div>

                <div className="space-y-6" data-id="element-3036">
                  {[{
                icon: Gift,
                title: 'Daily Packets',
                desc: 'Come back daily to open a new packet and grow your collection.'
              }, {
                icon: Repeat,
                title: 'Complete Sets',
                desc: 'Trade duplicates with others to complete historical sets.'
              }, {
                icon: Crown,
                title: 'Rise in Rank',
                desc: 'Unlock real-world rewards and privileges as you gain XP.'
              }].map((item, i) => <div key={i} className="flex gap-5" data-id="element-3037">
                      <div className="w-12 h-12 shrink-0 border border-[#D4AF37]/30 bg-[#0B0F19] flex items-center justify-center" data-id="element-3038">
                        <item.icon className="w-5 h-5 text-[#D4AF37]" data-id="element-3039" />
                      </div>
                      <div className="space-y-1" data-id="element-3040">
                        <h4 className="text-[#FDFBF7] text-sm font-bold uppercase tracking-wider" data-id="element-3041">
                          {item.title}
                        </h4>
                        <p className="text-[#FDFBF7]/50 text-xs leading-relaxed" data-id="element-3042">
                          {item.desc}
                        </p>
                      </div>
                    </div>)}
                </div>

                <button onClick={nextStep} className="w-full py-4 bg-[#D4AF37] text-[#0B0F19] text-xs uppercase tracking-[0.2em] font-bold hover:bg-[#E5C158] transition-colors flex items-center justify-center gap-2" data-id="element-3043">
                  Got It <ArrowRight className="w-4 h-4" data-id="element-3044" />
                </button>
              </motion.div>}

            {/* Step 9: Misl Path */}
            {step === 'misl' && <motion.div key="misl" initial={{
            opacity: 0,
            x: 20
          }} animate={{
            opacity: 1,
            x: 0
          }} exit={{
            opacity: 0,
            x: -20
          }} className="space-y-6" data-id="element-3045">
                <AnimatePresence mode="wait" data-id="element-3046">
                  {/* ============ CHOOSE ============ */}
                  {mislView === 'choose' && <motion.div key="choose" initial={{
                opacity: 0,
                x: 10
              }} animate={{
                opacity: 1,
                x: 0
              }} exit={{
                opacity: 0,
                x: -10
              }} className="space-y-6" data-id="element-3047">
                      <div className="space-y-2" data-id="element-3048">
                        <h2 className="font-serif text-3xl text-[#FDFBF7]" data-id="element-3049">
                          Join Your Misl{' '}
                          <span className="text-[#FDFBF7]/40" data-id="element-3050">(Clan)</span>
                        </h2>
                        <p className="text-[#FDFBF7]/60 text-sm" data-id="element-3051">
                          The Sikh Confederacy was built on Misls — clans and
                          sovereign states of the Khalsa. Join one to grow
                          faster.
                        </p>
                      </div>

                      <div className="grid gap-3" data-id="element-3052">
                        <button type="button" onClick={() => {
                    setSelectedMisl('Ahluwalia');
                    setMislView('confirm-invite');
                  }} className="group flex items-center gap-4 p-5 border border-[#D4AF37] bg-[#D4AF37]/10 hover:bg-[#D4AF37]/20 transition-all text-left" data-id="element-3053">
                          <div className="w-10 h-10 border border-[#D4AF37] flex items-center justify-center" data-id="element-3054">
                            <Check className="w-5 h-5 text-[#D4AF37]" data-id="element-3055" />
                          </div>
                          <div className="flex-1" data-id="element-3056">
                            <p className="text-[#FDFBF7] text-sm font-bold" data-id="element-3057">
                              Join with my invite
                            </p>
                            <p className="text-[#FDFBF7]/40 text-[10px] uppercase tracking-widest mt-0.5" data-id="element-3058">
                              Use your referral link
                            </p>
                          </div>
                          <ChevronRight className="w-4 h-4 text-[#D4AF37]/60" data-id="element-3059" />
                        </button>
                        <button type="button" onClick={() => setMislView('browse')} className="group flex items-center gap-4 p-5 border border-[#D4AF37]/20 bg-[#0B0F19]/40 hover:border-[#D4AF37]/50 hover:bg-[#D4AF37]/5 transition-all text-left" data-id="element-3060">
                          <div className="w-10 h-10 border border-[#D4AF37]/30 flex items-center justify-center group-hover:border-[#D4AF37]/60 transition-colors" data-id="element-3061">
                            <Users className="w-5 h-5 text-[#D4AF37]/60 group-hover:text-[#D4AF37] transition-colors" data-id="element-3062" />
                          </div>
                          <div className="flex-1" data-id="element-3063">
                            <p className="text-[#FDFBF7] text-sm font-bold" data-id="element-3064">
                              Browse active clans (Misls)
                            </p>
                            <p className="text-[#FDFBF7]/40 text-[10px] uppercase tracking-widest mt-0.5" data-id="element-3065">
                              Find your community
                            </p>
                          </div>
                          <ChevronRight className="w-4 h-4 text-[#D4AF37]/40 group-hover:text-[#D4AF37]/80 transition-colors" data-id="element-3066" />
                        </button>
                      </div>
                    </motion.div>}

                  {/* ============ BROWSE ============ */}
                  {mislView === 'browse' && <motion.div key="browse" initial={{
                opacity: 0,
                x: 10
              }} animate={{
                opacity: 1,
                x: 0
              }} exit={{
                opacity: 0,
                x: -10
              }} className="space-y-5" data-id="element-3067">
                      <button type="button" onClick={() => setMislView('choose')} className="flex items-center gap-1.5 text-[#FDFBF7]/50 hover:text-[#D4AF37] text-[10px] uppercase tracking-[0.25em] font-medium transition-colors" data-id="element-3068">
                        <ChevronLeft className="w-3 h-3" data-id="element-3069" /> Back to options
                      </button>

                      <div className="space-y-2" data-id="element-3070">
                        <h2 className="font-serif text-2xl sm:text-3xl text-[#FDFBF7]" data-id="element-3071">
                          Choose Your Clan{' '}
                          <span className="text-[#FDFBF7]/40 text-xl" data-id="element-3072">
                            (Misl)
                          </span>
                        </h2>
                        <p className="text-[#FDFBF7]/60 text-sm" data-id="element-3073">
                          Pick a sovereign state of the Khalsa to join. You can
                          switch later.
                        </p>
                      </div>

                      {/* Leadership rule callout */}
                      <div className="flex items-start gap-3 p-3.5 border border-[#D4AF37]/30 bg-[#D4AF37]/5" data-id="element-3074">
                        <Crown className="w-4 h-4 text-[#D4AF37] mt-0.5 shrink-0" data-id="element-3075" />
                        <p className="text-[#FDFBF7]/80 text-xs leading-relaxed" data-id="element-3076">
                          The member with the most{' '}
                          <span className="text-[#D4AF37] font-bold" data-id="element-3077">XP</span>{' '}
                          in each clan becomes its{' '}
                          <span className="text-[#D4AF37] font-bold" data-id="element-3078">
                            Misldar
                          </span>{' '}
                          (Clan Chief). Earn the most to lead.
                        </p>
                      </div>

                      <div className="grid gap-2.5 max-h-[400px] overflow-y-auto pr-1" data-id="element-3079">
                        {misls.map(m => {
                    const isSelected = selectedMisl === m.name;
                    return <button key={m.name} type="button" onClick={() => setSelectedMisl(m.name)} className={`group relative flex items-stretch gap-4 p-4 border transition-all text-left ${isSelected ? 'border-[#D4AF37] bg-[#D4AF37]/10 shadow-[0_0_20px_rgba(212,175,55,0.15)]' : 'border-[#D4AF37]/20 bg-[#0B0F19]/40 hover:border-[#D4AF37]/50 hover:bg-[#D4AF37]/5'}`} data-id="element-3080">
                              {/* Crest */}
                              <div className={`w-12 h-12 shrink-0 border flex items-center justify-center transition-colors ${isSelected ? 'border-[#D4AF37] bg-[#D4AF37]/15' : 'border-[#D4AF37]/30 group-hover:border-[#D4AF37]/60'}`} data-id="element-3081">
                                <Shield className={`w-5 h-5 ${isSelected ? 'text-[#D4AF37]' : 'text-[#D4AF37]/60'}`} data-id="element-3082" />
                              </div>

                              {/* Info */}
                              <div className="flex-1 min-w-0" data-id="element-3083">
                                <div className="flex items-center gap-2 flex-wrap" data-id="element-3084">
                                  <p className="text-[#FDFBF7] text-sm font-bold" data-id="element-3085">
                                    {m.name}
                                  </p>
                                  <span className="text-[#D4AF37]/60 text-[8px] uppercase tracking-[0.25em] border border-[#D4AF37]/25 px-1.5 py-0.5" data-id="element-3086">
                                    {m.tag}
                                  </span>
                                </div>
                                <p className="text-[#FDFBF7]/40 text-[10px] font-mono mt-0.5" data-id="element-3087">
                                  Led by {m.leader} · {m.region}
                                </p>
                                <div className="flex items-center gap-3 mt-2 text-[9px] uppercase tracking-widest text-[#FDFBF7]/50" data-id="element-3088">
                                  <span className="flex items-center gap-1" data-id="element-3089">
                                    <Users className="w-2.5 h-2.5 text-[#D4AF37]/50" data-id="element-3090" />
                                    {m.members} members
                                  </span>
                                  <span className="text-[#D4AF37]/20" data-id="element-3091">·</span>
                                  <span className="flex items-center gap-1" data-id="element-3092">
                                    <Sparkles className="w-2.5 h-2.5 text-[#D4AF37]/50" data-id="element-3093" />
                                    {m.xp} XP
                                  </span>
                                </div>
                              </div>

                              {isSelected && <Check className="absolute top-3 right-3 w-4 h-4 text-[#D4AF37]" data-id="element-3094" />}
                            </button>;
                  })}
                      </div>

                      <button type="button" onClick={() => selectedMisl && nextStep()} disabled={!selectedMisl} className={`w-full py-4 text-xs uppercase tracking-[0.2em] font-bold transition-colors flex items-center justify-center gap-2 group ${selectedMisl ? 'bg-[#D4AF37] text-[#0B0F19] hover:bg-[#E5C158] shadow-[0_0_30px_rgba(212,175,55,0.3)]' : 'bg-[#D4AF37]/20 text-[#0B0F19]/40 cursor-not-allowed'}`} data-id="element-3095">
                        {selectedMisl ? `Join ${selectedMisl}` : 'Select a clan'}
                        {selectedMisl && <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" data-id="element-3096" />}
                      </button>
                    </motion.div>}

                  {/* ============ CONFIRM INVITE ============ */}
                  {mislView === 'confirm-invite' && <motion.div key="confirm-invite" initial={{
                opacity: 0,
                scale: 0.95
              }} animate={{
                opacity: 1,
                scale: 1
              }} exit={{
                opacity: 0,
                scale: 0.95
              }} className="space-y-6 text-center" data-id="element-3097">
                      <button type="button" onClick={() => setMislView('choose')} className="flex items-center gap-1.5 text-[#FDFBF7]/50 hover:text-[#D4AF37] text-[10px] uppercase tracking-[0.25em] font-medium transition-colors" data-id="element-3098">
                        <ChevronLeft className="w-3 h-3" data-id="element-3099" /> Back
                      </button>

                      <div className="space-y-2 pt-2" data-id="element-3100">
                        <p className="text-[#D4AF37] text-[10px] uppercase tracking-[0.3em] font-bold" data-id="element-3101">
                          Welcome to the Court
                        </p>
                        <h2 className="font-serif text-2xl sm:text-3xl text-[#FDFBF7]" data-id="element-3102">
                          You've joined {selectedMisl}
                        </h2>
                      </div>

                      <div className="relative py-2" data-id="element-3103">
                        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_rgba(212,175,55,0.2),_transparent_70%)] blur-2xl" data-id="element-3104" />
                        <div className="relative w-24 h-24 mx-auto border-2 border-[#D4AF37] bg-[#0B0F19] flex items-center justify-center shadow-[0_0_30px_rgba(212,175,55,0.3)]" data-id="element-3105">
                          <Shield className="w-12 h-12 text-[#D4AF37]" data-id="element-3106" />
                        </div>
                      </div>

                      <p className="text-[#FDFBF7]/60 text-sm max-w-xs mx-auto leading-relaxed" data-id="element-3107">
                        Your invite is honored. You'll find your fellow members
                        on the dashboard.
                      </p>

                      <button type="button" onClick={nextStep} className="w-full py-4 bg-[#D4AF37] text-[#0B0F19] text-xs uppercase tracking-[0.2em] font-bold hover:bg-[#E5C158] transition-colors flex items-center justify-center gap-2 group shadow-[0_0_30px_rgba(212,175,55,0.3)]" data-id="element-3108">
                        Continue
                        <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" data-id="element-3109" />
                      </button>
                    </motion.div>}
                </AnimatePresence>
              </motion.div>}

            {/* Step 10: WhatsApp Growth Prompt */}
            {step === 'share' && <motion.div key="share" initial={{
            opacity: 0,
            x: 20
          }} animate={{
            opacity: 1,
            x: 0
          }} exit={{
            opacity: 0,
            x: -20
          }} className="space-y-7" data-id="element-3110">
                <div className="space-y-2 text-center" data-id="element-3111">
                  <p className="text-[#D4AF37] text-[10px] uppercase tracking-[0.3em] font-bold" data-id="element-3112">
                    Final Step
                  </p>
                  <h2 className="font-serif text-3xl text-[#FDFBF7]" data-id="element-3113">
                    Bring Others In
                  </h2>
                  <p className="text-[#FDFBF7]/60 text-sm max-w-sm mx-auto" data-id="element-3114">
                    This is the invitation your friends and family will receive.
                    Personal, premium, and historically grounded.
                  </p>
                </div>

                {/* Plain-text invitation message */}
                {(() => {
              const origin = typeof window !== 'undefined' ? window.location.origin : 'https://lahore-treasury.app';
              const inviterName = 'Harnoor';
              const inviterCode = 'HARNOOR-123';
              const mislName = selectedMisl || 'Lahore';
              // Clean vanity URL that appears in the shared text + preview.
              const displayLink = `https://lahore-treasury.app/join/${inviterCode}`;
              // Actual working URL (drops them into onboarding pre-filled).
              // Used by the clickable anchor inside the preview for the demo.
              const actualLink = `${origin}/treasure?ref=${inviterCode}&from=${encodeURIComponent(inviterName)}&misl=${encodeURIComponent(mislName)}`;
              const intro = `You're invited to Treasures of the Sikh Empire — a premium collectible experience built around the Sikh Empire.\n\nI've just joined ${mislName}. Every artifact (Maharajas, generals, jewels, fortresses) carries real historical weight. Open daily packets, complete sets, and build a treasury that means something.\n\nJoin me here and claim your free Founders' Edition card:\n`;
              // Full message used for WhatsApp share + clipboard copy.
              const inviteMessage = intro + displayLink;
              return <>
                      <motion.div initial={{
                  opacity: 0,
                  y: 12
                }} animate={{
                  opacity: 1,
                  y: 0
                }} transition={{
                  duration: 0.5,
                  delay: 0.1
                }} className="bg-[#0B0F19] border border-[#D4AF37]/20 p-5 sm:p-6" data-id="element-3115">
                        <p className="text-[#FDFBF7]/80 text-sm leading-relaxed whitespace-pre-line text-left" data-id="element-3116">
                          {intro}
                          <a href={actualLink} target="_blank" rel="noopener noreferrer" className="text-[#D4AF37] underline decoration-[#D4AF37]/40 underline-offset-2 hover:decoration-[#D4AF37] break-all" data-id="element-3117">
                            {displayLink}
                          </a>
                        </p>
                      </motion.div>

                      {/* Action buttons */}
                      <div className="space-y-3" data-id="element-3118">
                        <a href={`https://wa.me/?text=${encodeURIComponent(inviteMessage)}`} target="_blank" rel="noopener noreferrer" className="w-full py-4 bg-[#25D366] text-white text-xs uppercase tracking-[0.2em] font-bold hover:opacity-90 transition-opacity flex items-center justify-center gap-2 shadow-lg" data-id="element-3119">
                          <MessageCircle className="w-4 h-4" data-id="element-3120" /> Share on
                          WhatsApp
                        </a>
                        <button onClick={() => {
                    navigator.clipboard.writeText(inviteMessage);
                    alert('Invitation copied!');
                  }} className="w-full py-4 border border-[#D4AF37]/30 text-[#FDFBF7] text-xs uppercase tracking-[0.2em] font-medium hover:bg-[#D4AF37]/10 transition-colors flex items-center justify-center gap-2" data-id="element-3121">
                          <Copy className="w-4 h-4" data-id="element-3122" /> Copy Invite Link
                        </button>
                        <button onClick={() => setShowSkipShareConfirm(true)} className="w-full py-4 text-[#FDFBF7]/30 text-[10px] uppercase tracking-[0.2em] font-medium hover:text-[#FDFBF7]/60 transition-colors" data-id="element-3123">
                          Skip for now
                        </button>
                      </div>

                      {/* Emotional skip-share intercept */}
                      <AnimatePresence data-id="element-3124">
                        {showSkipShareConfirm && <motion.div key="skip-confirm" initial={{
                    opacity: 0
                  }} animate={{
                    opacity: 1
                  }} exit={{
                    opacity: 0
                  }} className="absolute inset-0 z-20 flex items-center justify-center p-4 sm:p-6 bg-[#0B0F19]/85 backdrop-blur-sm" data-id="element-3125">
                            <motion.div initial={{
                      opacity: 0,
                      scale: 0.94,
                      y: 16
                    }} animate={{
                      opacity: 1,
                      scale: 1,
                      y: 0
                    }} exit={{
                      opacity: 0,
                      scale: 0.94,
                      y: 16
                    }} transition={{
                      duration: 0.35,
                      ease: 'easeOut'
                    }} className="relative w-full max-w-md bg-gradient-to-b from-[#131B2B] to-[#0B0F19] border border-[#D4AF37]/40 shadow-[0_30px_80px_rgba(0,0,0,0.7)] p-7 sm:p-8 text-center space-y-6" data-id="element-3126">
                              {/* Decorative gold seam */}
                              <div className="absolute -top-px left-6 right-6 h-px bg-gradient-to-r from-transparent via-[#D4AF37]/70 to-transparent" data-id="element-3127" />

                              <div className="relative w-14 h-14 mx-auto" data-id="element-3128">
                                <motion.div animate={{
                          scale: [1, 1.15, 1],
                          opacity: [0.5, 0.9, 0.5]
                        }} transition={{
                          duration: 2.4,
                          repeat: Infinity,
                          ease: 'easeInOut'
                        }} className="absolute inset-0 rounded-full bg-[radial-gradient(circle_at_center,_rgba(212,175,55,0.55),_transparent_70%)] blur-xl" data-id="element-3129" />
                                <div className="relative w-full h-full border border-[#D4AF37] bg-[#0B0F19] flex items-center justify-center shadow-[0_0_30px_rgba(212,175,55,0.45)]" data-id="element-3130">
                                  <Heart className="w-6 h-6 text-[#D4AF37]" data-id="element-3131" />
                                </div>
                              </div>

                              <div className="space-y-3" data-id="element-3132">
                                <p className="text-[#D4AF37] text-[10px] uppercase tracking-[0.3em] font-bold" data-id="element-3133">
                                  Before you go
                                </p>
                                <h3 className="font-serif text-2xl sm:text-[28px] text-[#FDFBF7] leading-tight" data-id="element-3134">
                                  Don't let this end with you
                                </h3>
                                <p className="text-[#FDFBF7]/70 text-sm leading-relaxed" data-id="element-3135">
                                  For two hundred years, our stories have been
                                  kept alive by one thing — someone choosing to
                                  pass them on.
                                </p>
                                <p className="text-[#FDFBF7]/70 text-sm leading-relaxed" data-id="element-3136">
                                  One share. One cousin, one friend, one elder
                                  who deserves to see this. That's how the
                                  Lahore Darbar lives again — through{' '}
                                  <span className="text-[#D4AF37] font-medium" data-id="element-3137">
                                    you
                                  </span>
                                  .
                                </p>
                                <p className="text-[#FDFBF7]/50 text-[11px] italic pt-1" data-id="element-3138">
                                  Every founding member who shares brings the
                                  court one step closer to home.
                                </p>
                              </div>

                              <div className="space-y-2.5 pt-1" data-id="element-3139">
                                <button type="button" onClick={() => setShowSkipShareConfirm(false)} className="w-full py-3.5 bg-[#D4AF37] text-[#0B0F19] text-xs uppercase tracking-[0.2em] font-bold hover:bg-[#E5C158] transition-colors flex items-center justify-center gap-2 shadow-[0_0_30px_rgba(212,175,55,0.35)]" data-id="element-3140">
                                  <Sparkles className="w-4 h-4" data-id="element-3141" />
                                  Yes — I'll share
                                </button>
                                <button type="button" onClick={() => {
                          setShowSkipShareConfirm(false);
                          nextStep();
                        }} className="w-full py-3 text-[#FDFBF7]/35 text-[10px] uppercase tracking-[0.25em] font-medium hover:text-[#FDFBF7]/60 transition-colors" data-id="element-3142">
                                  Maybe later, continue alone
                                </button>
                              </div>
                            </motion.div>
                          </motion.div>}
                      </AnimatePresence>
                    </>;
            })()}
              </motion.div>}

            {/* Step 11: Complete */}
            {step === 'complete' && <motion.div key="complete" initial={{
            opacity: 0,
            scale: 0.9
          }} animate={{
            opacity: 1,
            scale: 1
          }} className="space-y-8 text-center" data-id="element-3143">
                <div className="relative py-4" data-id="element-3144">
                  <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_rgba(212,175,55,0.3),_transparent_70%)] blur-3xl" data-id="element-3145" />
                  <motion.div initial={{
                scale: 0
              }} animate={{
                scale: 1
              }} className="w-20 h-20 mx-auto bg-[#D4AF37] rounded-full flex items-center justify-center shadow-[0_0_30px_rgba(212,175,55,0.5)]" data-id="element-3146">
                    <Check className="w-10 h-10 text-[#0B0F19]" data-id="element-3147" />
                  </motion.div>
                </div>

                <div className="space-y-2" data-id="element-3148">
                  <h2 className="font-serif text-3xl text-[#FDFBF7]" data-id="element-3149">
                    Welcome to the Court
                  </h2>
                  <p className="text-[#FDFBF7]/60 text-sm" data-id="element-3150">
                    Your journey has officially begun.
                  </p>
                </div>

                <button onClick={onComplete} className="w-full py-4 bg-[#D4AF37] text-[#0B0F19] text-xs uppercase tracking-[0.2em] font-bold hover:bg-[#E5C158] transition-colors flex items-center justify-center gap-2" data-id="element-3151">
                  Enter Dashboard <ArrowRight className="w-4 h-4" data-id="element-3152" />
                </button>
              </motion.div>}
          </AnimatePresence>
        </div>
      </motion.div>
    </div>;
}
// Re-exporting icons for use in parent if needed
export { Repeat } from 'lucide-react';